<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="checkout-overlay" @click.self="onBackdropClick">
        <div class="checkout-card" role="dialog" aria-modal="true">
          <!-- Header -->
          <div class="head">
            <button class="ghost-btn" @click="close">
              <VaIcon name="close" size="18px" />
            </button>
            <div class="brand-pill">Secure checkout</div>
          </div>

          <!-- Loading -->
          <div v-if="loading && !payment" class="state-block">
            <VaProgressCircle indeterminate />
            <p class="muted">Loading payment…</p>
          </div>

          <!-- Not found -->
          <div v-else-if="!payment" class="state-block">
            <VaIcon name="error_outline" size="32px" color="danger" />
            <h3>Payment not found</h3>
            <p class="muted">
              Reference <code>{{ reference }}</code> doesn't match any payment.
            </p>
            <VaButton preset="primary" @click="close">Close</VaButton>
          </div>

          <template v-else>
            <!-- Title row -->
            <div class="title-row">
              <div>
                <div class="eyebrow">{{ subscription?.service_name }}</div>
                <h1 class="plan-title">{{ subscription?.plan_name }}</h1>
                <div class="muted small">
                  {{ subscription?.plan_duration }} {{ subscription?.plan_duration_type?.toLowerCase() }} ·
                  {{ methodLabel }}
                </div>
              </div>
              <div class="status-badge" :data-status="payment.status">
                <span class="dot" />
                {{ statusLabel }}
              </div>
            </div>

            <!-- Amount -->
            <div class="amount-block">
              <div class="charge">
                <span class="charge-currency">TSh</span>
                <span class="charge-value">{{ formatTzs(payment.amount) }}</span>
              </div>
              <div class="muted small">
                ≈ ${{ payment.source_amount }} USD
                <span v-if="payment.fx_rate" class="dot-sep">·</span>
                <span v-if="payment.fx_rate">FX 1 USD = {{ Number(payment.fx_rate).toFixed(2) }} TZS</span>
              </div>
            </div>

            <!-- Meta -->
            <div class="meta-grid">
              <div>
                <div class="muted micro">Reference</div>
                <div class="mono">{{ payment.snippe_reference }}</div>
              </div>
              <div v-if="payment.phone_number">
                <div class="muted micro">Phone</div>
                <div>{{ payment.phone_number }}</div>
              </div>
              <div>
                <div class="muted micro">Created</div>
                <div>{{ formatDate(payment.created_at) }}</div>
              </div>
            </div>

            <!-- Per-status / per-method instruction -->
            <div v-if="isTerminal" class="instructions" :data-status="payment.status">
              <VaIcon :name="terminalIcon" size="22px" />
              <div>
                <h4>{{ terminalTitle }}</h4>
                <p class="muted small">{{ terminalBody }}</p>
              </div>
            </div>

            <div v-else-if="payment.method === 'mobile_money'" class="instructions" data-status="pending">
              <VaIcon name="phone_android" size="22px" />
              <div>
                <h4>Approve the USSD prompt</h4>
                <p class="muted small">
                  We sent a request to <span class="mono">{{ payment.phone_number }}</span
                  >. Open your phone and approve the prompt to complete payment.
                </p>
              </div>
            </div>

            <div v-else-if="payment.method === 'credit_card'" class="instructions" data-status="pending">
              <VaIcon name="credit_card" size="22px" />
              <div>
                <h4>Continue to the secure card page</h4>
                <p class="muted small">
                  Click below to complete payment on the gateway. We'll keep watching here — close this when you're
                  done.
                </p>
                <VaButton v-if="payment.payment_url" size="small" preset="primary" class="mt-2" @click="openCardPage">
                  <VaIcon name="open_in_new" size="14px" class="mr-1" />
                  Open payment page
                </VaButton>
              </div>
            </div>

            <!-- Auto-poll -->
            <div v-if="!isTerminal" class="poll-row">
              <VaProgressCircle indeterminate size="small" />
              <span class="muted small">Watching for confirmation… auto-refresh every 4s</span>
              <button class="ghost-btn ml-auto" :disabled="polling" @click="refresh()">
                <VaIcon name="refresh" size="14px" />
                <span>Refresh</span>
              </button>
            </div>

            <!-- Footer actions -->
            <div class="actions">
              <template v-if="payment.status === 'completed'">
                <VaButton preset="primary" @click="close">Done</VaButton>
              </template>
              <template v-else-if="payment.status === 'failed' || payment.status === 'expired'">
                <VaButton preset="secondary" @click="close">Close</VaButton>
                <VaButton preset="primary" @click="retry">Try again</VaButton>
              </template>
              <template v-else-if="payment.status === 'voided'">
                <VaButton preset="primary" @click="close">Close</VaButton>
              </template>
              <template v-else>
                <VaButton preset="secondary" :loading="cancelling" @click="cancelPayment">Cancel payment</VaButton>
                <VaButton
                  v-if="payment.method === 'credit_card' && payment.payment_url"
                  preset="primary"
                  @click="openCardPage"
                >
                  Continue to payment
                </VaButton>
              </template>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { VaIcon, VaButton, VaProgressCircle, useToast } from 'vuestic-ui'
import { useServicesStore } from '../../stores/services-store'

const router = useRouter()
const { init: toast } = useToast()
const servicesStore = useServicesStore()

const reference = computed(() => servicesStore.checkoutReference)
const open = computed(() => !!reference.value)

const payment = ref<any>(null)
const subscription = ref<any>(null)
const loading = ref(false)
const polling = ref(false)
const cancelling = ref(false)
let timer: number | null = null

const isTerminal = computed(() => ['completed', 'failed', 'expired', 'voided'].includes(payment.value?.status))

const statusLabel = computed(() => {
  const map: Record<string, string> = {
    pending: 'Awaiting confirmation',
    processing: 'Processing',
    completed: 'Paid',
    failed: 'Failed',
    expired: 'Expired',
    voided: 'Cancelled',
  }
  return map[payment.value?.status] ?? payment.value?.status
})

const methodLabel = computed(() => {
  const map: Record<string, string> = {
    mobile_money: 'Mobile money',
    credit_card: 'Credit card',
    qr_code: 'QR code',
    free: 'Free trial',
  }
  return map[payment.value?.method] ?? payment.value?.method
})

const terminalIcon = computed(() => {
  if (payment.value?.status === 'completed') return 'check_circle'
  if (payment.value?.status === 'voided') return 'block'
  return 'error_outline'
})
const terminalTitle = computed(() => {
  if (payment.value?.status === 'completed') return `${subscription.value?.service_name} is now active`
  if (payment.value?.status === 'failed') return 'Payment failed'
  if (payment.value?.status === 'expired') return 'Payment timed out'
  if (payment.value?.status === 'voided') return 'Payment cancelled'
  return ''
})
const terminalBody = computed(() => {
  if (payment.value?.status === 'completed')
    return `Your subscription runs until ${formatDate(subscription.value?.end_date)}.`
  if (payment.value?.status === 'failed')
    return payment.value?.failure_reason || 'The payment provider declined this transaction.'
  if (payment.value?.status === 'expired') return 'No response was received in time. Try again to start a new payment.'
  if (payment.value?.status === 'voided') return 'You cancelled this payment. You can subscribe again anytime.'
  return ''
})

async function refresh(silent = false) {
  if (!reference.value) return
  if (!silent) polling.value = true
  try {
    const data = await servicesStore.getPaymentStatus(reference.value)
    payment.value = data.payment
    subscription.value = data.subscription
  } catch (err: any) {
    if (err?.response?.status === 404) payment.value = null
  } finally {
    polling.value = false
    loading.value = false
  }
}

function startPolling() {
  stopPolling()
  timer = window.setInterval(() => {
    if (!isTerminal.value) refresh(true)
    else stopPolling()
  }, 4000)
}
function stopPolling() {
  if (timer) {
    window.clearInterval(timer)
    timer = null
  }
}

function openCardPage() {
  if (payment.value?.payment_url) window.open(payment.value.payment_url, '_blank', 'noopener')
}

async function cancelPayment() {
  if (!reference.value) return
  cancelling.value = true
  try {
    const url = import.meta.env.VITE_APP_API_BASE_URL + `services/payments/${reference.value}/cancel/`
    const { data } = await axios.post(url)
    payment.value = data.payment
    subscription.value = data.subscription
    toast({ message: 'Payment cancelled', color: 'info' })
  } catch (err: any) {
    toast({ message: err?.response?.data?.detail ?? 'Failed to cancel', color: 'danger' })
  } finally {
    cancelling.value = false
  }
}

function retry() {
  const sid = subscription.value?.service
  close()
  if (sid) router.push({ name: 'service-details', params: { id: sid }, query: { subscribe: 'true' } })
}

function close() {
  stopPolling()
  if (payment.value?.status === 'completed') {
    servicesStore.fetchSubscriptions(1, 50)
    servicesStore.fetchServices(1, 50)
  }
  payment.value = null
  subscription.value = null
  servicesStore.closeCheckout()
}

function onBackdropClick() {
  // Don't close if user is mid-flight on a payment
  if (isTerminal.value) close()
}

function formatTzs(v?: string | number) {
  if (v === null || v === undefined) return '—'
  const n = typeof v === 'string' ? parseFloat(v) : v
  return Math.round(n).toLocaleString('en-TZ')
}
function formatDate(d?: string) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

watch(reference, async (ref) => {
  if (ref) {
    loading.value = true
    payment.value = null
    await refresh()
    if (!isTerminal.value) startPolling()
    if (payment.value?.method === 'credit_card' && payment.value?.payment_url && payment.value?.status === 'pending') {
      // auto-launch the gateway page in a new tab
      setTimeout(openCardPage, 500)
    }
  } else {
    stopPolling()
  }
})

onBeforeUnmount(stopPolling)
</script>

<style scoped>
.checkout-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 18, 25, 0.45);
  backdrop-filter: blur(8px) saturate(140%);
  -webkit-backdrop-filter: blur(8px) saturate(140%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active .checkout-card,
.fade-leave-active .checkout-card {
  transition:
    transform 0.22s cubic-bezier(0.2, 0.7, 0.2, 1),
    opacity 0.18s ease;
}
.fade-enter-from .checkout-card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}
.fade-leave-to .checkout-card {
  transform: translateY(4px) scale(0.99);
  opacity: 0;
}

.checkout-card {
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 48px);
  overflow: auto;
  background: var(--va-background-primary, #fff);
  border: 1px solid var(--va-background-element, rgba(0, 0, 0, 0.06));
  border-radius: 16px;
  padding: 28px;
  box-shadow:
    0 20px 48px -12px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.02);
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 13px;
  color: var(--va-text-primary);
  opacity: 0.7;
  cursor: pointer;
  transition:
    opacity 0.15s,
    background 0.15s;
}
.ghost-btn:hover {
  opacity: 1;
  background: var(--va-background-element);
}
.ghost-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.brand-pill {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--va-primary) 14%, transparent);
  color: var(--va-primary);
  font-weight: 600;
}

.title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.eyebrow {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--va-text-primary);
  opacity: 0.55;
  margin-bottom: 4px;
}

.plan-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  letter-spacing: -0.01em;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid;
  white-space: nowrap;
}
.status-badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.status-badge[data-status='completed'] {
  background: color-mix(in srgb, #16a34a 10%, transparent);
  border-color: color-mix(in srgb, #16a34a 30%, transparent);
  color: #15803d;
}
.status-badge[data-status='completed'] .dot {
  background: #16a34a;
}

.status-badge[data-status='pending'],
.status-badge[data-status='processing'] {
  background: color-mix(in srgb, #f59e0b 10%, transparent);
  border-color: color-mix(in srgb, #f59e0b 35%, transparent);
  color: #b45309;
}
.status-badge[data-status='pending'] .dot,
.status-badge[data-status='processing'] .dot {
  background: #f59e0b;
  animation: pulse 1.4s ease-in-out infinite;
}

.status-badge[data-status='failed'],
.status-badge[data-status='expired'] {
  background: color-mix(in srgb, #dc2626 10%, transparent);
  border-color: color-mix(in srgb, #dc2626 30%, transparent);
  color: #b91c1c;
}
.status-badge[data-status='failed'] .dot,
.status-badge[data-status='expired'] .dot {
  background: #dc2626;
}

.status-badge[data-status='voided'] {
  background: color-mix(in srgb, var(--va-text-primary) 8%, transparent);
  border-color: color-mix(in srgb, var(--va-text-primary) 20%, transparent);
  color: var(--va-text-primary);
  opacity: 0.7;
}
.status-badge[data-status='voided'] .dot {
  background: var(--va-text-primary);
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.85);
  }
}

.amount-block {
  padding: 18px 0;
  border-top: 1px solid var(--va-background-element);
  border-bottom: 1px solid var(--va-background-element);
  margin-bottom: 20px;
}

.charge {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.charge-currency {
  font-size: 18px;
  opacity: 0.55;
}
.charge-value {
  font-size: 36px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  font-size: 13px;
  margin-bottom: 18px;
}

.instructions {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  border: 1px solid;
  background: color-mix(in srgb, var(--va-primary) 4%, transparent);
  border-color: color-mix(in srgb, var(--va-primary) 18%, transparent);
}
.instructions h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
}
.instructions[data-status='completed'] {
  background: color-mix(in srgb, #16a34a 6%, transparent);
  border-color: color-mix(in srgb, #16a34a 22%, transparent);
  color: #15803d;
}
.instructions[data-status='failed'],
.instructions[data-status='expired'] {
  background: color-mix(in srgb, #dc2626 5%, transparent);
  border-color: color-mix(in srgb, #dc2626 22%, transparent);
  color: #b91c1c;
}
.instructions[data-status='voided'] {
  background: var(--va-background-element);
  border-color: var(--va-background-element);
}

.poll-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  padding: 10px 14px;
  background: var(--va-background-element);
  border-radius: 8px;
  margin-bottom: 18px;
}

.actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.muted {
  color: var(--va-text-primary);
  opacity: 0.6;
}
.small {
  font-size: 12px;
}
.micro {
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
}
.dot-sep {
  margin: 0 4px;
  opacity: 0.5;
}
.ml-auto {
  margin-left: auto;
}
.mr-1 {
  margin-right: 4px;
}
.mt-2 {
  margin-top: 8px;
}

.state-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  text-align: center;
}
.state-block h3 {
  margin: 0;
  font-size: 18px;
}
</style>
